/**
 * Merges persisted column order with the default columns definition order.
 * Columns missing from the stored order are inserted at the position defined
 * in defaultColumnOrder, relative to columns already in the stored order.
 */
export function mergeColumnOrder(
  storedColumnOrder: string[],
  defaultColumnOrder: string[]
): string[] {
  const defaultOrderSet = new Set(defaultColumnOrder);
  const filteredStored = storedColumnOrder.filter((id) =>
    defaultOrderSet.has(id)
  );

  const storedSet = new Set(filteredStored);
  const missing = defaultColumnOrder.filter((id) => !storedSet.has(id));

  if (missing.length === 0) {
    return filteredStored;
  }

  return missing.reduce((result, columnId) => {
    const defaultIndex = defaultColumnOrder.indexOf(columnId);
    const insertAt = result.reduce((at, colId, index) => {
      const resultDefaultIndex = defaultColumnOrder.indexOf(colId);
      if (resultDefaultIndex !== -1 && resultDefaultIndex < defaultIndex) {
        return index + 1;
      }
      return at;
    }, 0);

    const next = [...result];
    next.splice(insertAt, 0, columnId);
    return next;
  }, filteredStored);
}

/**
 * Merges persisted column visibility with the defaults. Columns not in the
 * stored state use their default, and ids of removed columns are dropped.
 */
export function mergeColumnVisibility(
  storedVisibility: Record<string, boolean>,
  defaultVisibility: Record<string, boolean>,
  columnIds: string[]
): Record<string, boolean> {
  const known = new Set(columnIds);
  const stored = Object.fromEntries(
    Object.entries(storedVisibility).filter(([id]) => known.has(id))
  );
  return { ...defaultVisibility, ...stored };
}
